import { useCallback, useEffect, useRef } from 'react'
import FolderOutlined from '@mui/icons-material/FolderOutlined'
import ScienceOutlined from '@mui/icons-material/ScienceOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'

import { useToast } from '@/shared/toast/ToastProvider'

import { useAllocatedInstrumentsDashboard } from '../api/editor-viewer-dashboard-queries'
import { EDITOR_VIEWER_DASHBOARD_LAYOUT } from '../lib/editor-viewer-dashboard-layout'
import { AllocatedInstrumentsSection } from './AllocatedInstrumentsSection'
import { DashboardDirectInstrumentsSection } from './DashboardDirectInstrumentsSection'
import { DashboardMetricCard } from './DashboardMetricCard'
import { EditorViewerDashboardSkeleton } from './EditorViewerDashboardSkeleton'

const REFETCH_INTERVAL_MS = 30_000
const LOAD_ERROR_MESSAGE = 'Unable to load dashboard data. Please refresh.'

interface Props {
  role: 'EDITOR' | 'VIEWER'
}

/** Sensemaker Editor / Viewer dashboard — Figma `5106:10346`. */
export function EditorViewerDashboard({ role }: Readonly<Props>) {
  const { showToast } = useToast()
  const errorToastShown = useRef(false)
  const canEdit = role === 'EDITOR'

  const { data, isLoading, isError, refetch } = useAllocatedInstrumentsDashboard({
    refetchIntervalMs: REFETCH_INTERVAL_MS,
  })

  const handleRetry = useCallback(() => {
    void refetch()
  }, [refetch])

  useEffect(() => {
    if (isError && !errorToastShown.current) {
      errorToastShown.current = true
      showToast(LOAD_ERROR_MESSAGE, 'error', undefined, {
        actionLabel: 'Retry',
        onAction: handleRetry,
      })
    }
    if (!isError) {
      errorToastShown.current = false
    }
  }, [isError, showToast, handleRetry])

  if (isLoading) {
    return <EditorViewerDashboardSkeleton />
  }

  if (isError) {
    return (
      <Box
        sx={{ py: 4, textAlign: 'center' }}
        data-testid="ev-dashboard-load-error"
      >
        <Button
          variant="outlined"
          onClick={() => void refetch()}
          data-testid="ev-dashboard-retry"
        >
          Retry
        </Button>
      </Box>
    )
  }

  const metrics = data?.metrics ?? {
    totalAllocatedCampaigns: 0,
    totalActiveInstruments: 0,
  }

  const metricCards = [
    {
      cardBg: '#daf5e4',
      iconBg: '#3f8367',
      Icon: FolderOutlined,
      value: metrics.totalAllocatedCampaigns,
      valueFormat: 'count' as const,
      label: 'Total Allocated Campaigns',
      labelColor: '#3f8367',
    },
    {
      cardBg: '#fae6dc',
      iconBg: '#df6534',
      Icon: ScienceOutlined,
      value: metrics.totalActiveInstruments,
      valueFormat: 'count' as const,
      label: 'Total Number of Active Instruments',
      labelColor: '#df6534',
    },
  ] as const

  return (
    <>
      <Grid
        container
        spacing={EDITOR_VIEWER_DASHBOARD_LAYOUT.metricGridGap}
        sx={{ mb: EDITOR_VIEWER_DASHBOARD_LAYOUT.metricsToSectionGap }}
      >
        {metricCards.map((m) => (
          <Grid key={m.label} item xs={12} md={6}>
            <DashboardMetricCard {...m} borderColor="#e5e7eb" />
          </Grid>
        ))}
      </Grid>

      <AllocatedInstrumentsSection rows={data?.rows ?? []} canEdit={canEdit} />
      <DashboardDirectInstrumentsSection canMutate={canEdit} />
    </>
  )
}
